export function findChild(org, childId) {
  if (org.id === childId) {
    return org;
  }
  for (const child of org.child_orgs) {
    const found = findChild(child, childId);
    if (found) {
      return found;
    }
  }
  return undefined;
}

// NOTE: this is only necessary because the native d3 node.find() isn't available
// with the current library versions (not sure if it's the d3 reference or @visx)
// TODO: remove the need for this
export function findNode(node, comparer) {
  if (comparer(node)) {
    return node;
  }

  for (const child of node.children ?? []) {
    const found = findNode(child, comparer);
    if (found) {
      return found;
    }
  }
  return undefined;
}

export function pathToChild(org, childId) {
  if (org.id === childId) {
    return [org];
  }

  for (const child of org.child_orgs ?? org.children) {
    const path = pathToChild(child, childId);
    if (path) {
      return [org, ...path];
    }
  }

  return undefined;
}

export function descendentsOfNode(node) {
  return node.children.flatMap((child) => {
    return [child, ...descendentsOfNode(child)];
  });
}

/** Path segment for org weight keys: `o:<id>` or `t:<id>`. */
export function orgWeightPathSegment(type, id) {
  const prefix = type === 'team' ? 't' : 'o';
  return `${prefix}:${id}`;
}

/** Joins parent path and segment into a root-relative path key. */
export function buildOrgWeightPathKey(parentPathKey, segment) {
  if (!parentPathKey) return segment;
  return `${parentPathKey}/${segment}`;
}

/**
 * Normalizes API org/team data into a UI tree. Includes `pathKey` for weight
 * placement identity (stable across depth changes; changes if reparented).
 */
export function normalizeOrg(
  orgOrTeam,
  type = 'org',
  depth = 0,
  parentPathKey = ''
) {
  const segment = orgWeightPathSegment(type, orgOrTeam.id);
  const pathKey = buildOrgWeightPathKey(parentPathKey, segment);
  const children = [
    ...(orgOrTeam.child_orgs?.map((child) =>
      normalizeOrg(child, 'org', depth + 1, pathKey)
    ) ?? []),
    ...(orgOrTeam.teams?.map((team) =>
      normalizeOrg(team, 'team', depth + 1, pathKey)
    ) ?? []),
  ];
  return {
    id: orgOrTeam.id,
    name: orgOrTeam.name,
    type,
    isRoot: orgOrTeam.is_root ?? false,
    depthId: `${depth}_${orgOrTeam.id}`,
    depth,
    pathKey,
    children,
  };
}

export function orgTreeNodeKind({ type, isRoot }) {
  if (type === 'team') {
    return 'team';
  }

  if (isRoot) {
    return 'organization';
  }

  return 'group';
}

export function orgTreeNodeKindLabel({ type, isRoot, depth = 0 }) {
  const kind = orgTreeNodeKind({ type, isRoot });

  if (kind === 'team') {
    return 'Team';
  }

  if (kind === 'organization') {
    return 'Organization';
  }

  return `Tier ${depth + 1} Group`;
}

/** Props for OrgTreeNodeTypeIcon (team, organization, or group). */
export function orgTreeNodeIconProps({ type, isRoot }) {
  if (type === 'team') {
    return { type: 'team' };
  }

  return { type: 'org', isRoot: Boolean(isRoot) };
}

export function allTeams(node) {
  if (node.type === 'team') {
    return [node];
  }

  return node.children.flatMap((c) => allTeams(c));
}
